/**
 * DroppableAIRefSection
 *
 * Renders 4 AI reference slots as droppable targets inside the
 * existing DndContext of ProductImagesSubTab. Each slot accepts
 * dragged product assets to set them as AI reference images.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useDroppable } from '@dnd-kit/core';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, Image as ImageIcon } from 'lucide-react';
import { Badge, Loading } from '@/components/v2/ui';
import { getAIReferenceImages, type AIRefSlot } from '@/lib/api/products';
import { AI_ROLES } from './AIReferencesSubTab';

interface DroppableAIRefSectionProps {
  productId: string;
  savingSlot: number | null;
}

export function DroppableAIRefSection({ productId, savingSlot }: DroppableAIRefSectionProps) {
  const { data } = useQuery({
    queryKey: ['ai-ref-slots', productId],
    queryFn: () => getAIReferenceImages(productId),
    staleTime: 30_000,
  });

  const slots = data?.slots ?? [];
  const filledCount = slots.filter((s) => !s.is_empty).length;

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-4 w-4 text-primary-500" />
        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
          KI-Referenzen
        </span>
        <Badge variant="secondary">{filledCount} / 4</Badge>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[1, 2, 3, 4].map((idx) => (
          <DroppableAIRefSlot
            key={idx}
            slot={slots.find((s) => s.slot === idx) ?? null}
            slotIndex={idx}
            saving={savingSlot === idx}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Single droppable slot (private)
// ============================================================================

function DroppableAIRefSlot({
  slot,
  slotIndex,
  saving,
}: {
  slot: AIRefSlot | null;
  slotIndex: number;
  saving: boolean;
}) {
  const isEmpty = !slot || slot.is_empty;
  const roleInfo = AI_ROLES.find(
    (r) => r.value.toLowerCase() === slot?.role?.toLowerCase(),
  );

  const { setNodeRef, isOver } = useDroppable({
    id: `ai-ref-slot-${slotIndex}`,
    data: { type: 'ai-ref-slot', slot: slotIndex },
  });

  const ringClass = isOver
    ? isEmpty
      ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20'
      : 'ring-2 ring-amber-500 bg-amber-50 dark:bg-amber-900/20'
    : '';

  return (
    <div
      ref={setNodeRef}
      className={`relative rounded-lg border border-neutral-200 dark:border-neutral-700 p-2 flex flex-col items-center justify-center min-h-[90px] transition-all ${
        isEmpty ? 'border-dashed' : ''
      } ${ringClass}`}
    >
      {saving ? (
        <Loading size="sm" />
      ) : isEmpty ? (
        <EmptySlotContent isOver={isOver} slotIndex={slotIndex} />
      ) : (
        <FilledSlotContent slot={slot} roleInfo={roleInfo} isOver={isOver} />
      )}
    </div>
  );
}

function EmptySlotContent({ isOver, slotIndex }: { isOver: boolean; slotIndex: number }) {
  return (
    <>
      <ImageIcon className="h-5 w-5 text-neutral-400 dark:text-neutral-500 mb-1" />
      <span className="text-xs text-neutral-500 dark:text-neutral-400 text-center">
        {isOver ? 'Loslassen' : `Slot ${slotIndex}`}
      </span>
    </>
  );
}

function FilledSlotContent({
  slot,
  roleInfo,
  isOver,
}: {
  slot: AIRefSlot;
  roleInfo: typeof AI_ROLES[number] | undefined;
  isOver: boolean;
}) {
  return (
    <>
      <div className="w-12 h-12 rounded overflow-hidden bg-neutral-100 dark:bg-neutral-800 mb-1">
        {slot.thumbnail_url ? (
          <img src={slot.thumbnail_url} alt={`Slot ${slot.slot}`} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="h-4 w-4 text-neutral-400" />
          </div>
        )}
      </div>
      {roleInfo && (
        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-2xs font-medium text-white ${roleInfo.color}`}>
          {roleInfo.icon} {roleInfo.label}
        </span>
      )}
      {isOver && (
        <span className="text-2xs text-amber-600 dark:text-amber-400 mt-0.5">Ersetzen</span>
      )}
    </>
  );
}
